import React from 'react';
import { LayoutProvider } from '@webV2/providers/LayoutProvider/LayoutProvider';
import { ReserveProvider } from '@webV2/providers/ReserveProvider/ReserveProvider';
import { ThemeProvider } from '@gettactic/components/themes/ThemeProvider/ThemeProvider';
import { MainSidebar } from '@webV2/components/sidebars/MainSidebar/MainSidebar';
import { ReserveModalProvider } from '@/hooks/useReserveModal';
import { useAuthenticated, useOrganizationSettings } from '@gettactic/hooks';
import { applyThemeColors, applyThemeFont } from '@webV2/layouts/theme';
import { ImpersonateProvider } from '@webV2/providers/ImpersonateProvider';
import { useMobileAppMessages } from '@gettactic/helpers/src/webview/useMobileAppMessages';
import { useRouter } from 'next/router';

const classes = {
  Wrapper: 'app-main-layout flex h-screen w-full',
  Sidebar:
    'app-main-layout-sidebar md:border-r md:border-r-neutral-300 bg-v2-sidebar text-v2-sidebar-foreground',
  MainContent: 'app-main-layout-content flex-1 md:mt-0 mt-12'
};

export const MainLayoutInner: React.FC<React.PropsWithChildren> = ({
  children
}) => {
  const router = useRouter();
  useMobileAppMessages((message) => {
    switch (message.action) {
      case 'navigate':
        switch (message.screen) {
          case 'user-settings':
            router.push('/settings/profile');
            break;
        }
        break;

      case 'openUrl':
        window.open(message.url, '_blank');
        break;
    }
  });
  return <div className={classes.MainContent}>{children}</div>;
};

export const AppMainLayout: React.FC<React.PropsWithChildren> = ({
  children
}) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const organizationId = authenticatedUser?.organization?.id;

  const { data: organizationSettings } = useOrganizationSettings({
    id: organizationId,
    refetchInterval: 1000 * 60 // 1 minute
  });

  React.useEffect(() => {
    if (organizationSettings?.theme_settings?.colors) {
      applyThemeColors(organizationSettings.theme_settings.colors);
    }

    if (organizationSettings?.theme_settings?.font) {
      applyThemeFont(organizationSettings.theme_settings.font);
    }
  }, [organizationSettings]);

  return (
    <ThemeProvider
      defaultBrand="base"
      storageKey="ui-theme-new"
      externalBrand="base"
    >
      <LayoutProvider>
        <ReserveProvider>
          <ReserveModalProvider>
            <ImpersonateProvider>
              <div className={classes.Wrapper}>
                <div className={classes.Sidebar}>
                  <MainSidebar />
                </div>
                <MainLayoutInner>{children}</MainLayoutInner>
              </div>
            </ImpersonateProvider>
          </ReserveModalProvider>
        </ReserveProvider>
      </LayoutProvider>
    </ThemeProvider>
  );
};
